/**
 * AuthorizationForm.jsx - Create/Edit authorization form
 * 
 * Sprint D — Authorization Light
 * Polymorphic scope fields based on authorization type
 * 
 * Features:
 * - Type-specific scope fields (STS, LUC, PDRA, CUSTOM)
 * - Date pickers for issued_at, valid_from, valid_until
 * - SAIL level multi-select
 * - Conditions tag input
 * - PDF upload
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../lib/api';
import { useOrganizationScope } from '../contexts/OrganizationScopeContext';
import { 
  Award, Save, X, Plus, Upload, FileText, Calendar, Tag, Info
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { PageShell } from '../components/layout/PageShell';
import cs from '../locales/cs';
import en from '../locales/en';

const SAIL_LEVELS = ['I', 'II', 'III', 'IV', 'V', 'VI'];
const AUTH_TYPES = ['STS_01', 'STS_02', 'PDRA_S01', 'PDRA_S02', 'PDRA_G01', 'LUC', 'OPERATIONAL_AUTHORIZATION', 'CUSTOM'];
const DRONE_CLASSES = ['C0', 'C1', 'C2', 'C3', 'C4', 'C5', 'C6'];
const AIRSPACE_CLASSES = ['A', 'B', 'C', 'D', 'E', 'F', 'G'];

export default function AuthorizationForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;
  const { registerScopeChangeGuard } = useOrganizationScope();

  const [language, setLanguage] = useState(localStorage.getItem('language') || 'cs');
  const t = language === 'cs' ? cs.authorizations : en.authorizations;
  const tCommon = language === 'cs' ? cs.common : en.common;
  
  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  // Form state
  const [form, setForm] = useState({
    type: 'STS_01',
    reference_number: '',
    title: '',
    issued_by: 'ÚCL',
    issued_at: '',
    valid_from: '',
    valid_until: '',
    conditions: [],
    applicable_sail_levels: [],
    aircraft_ids: [],
    notes: '',
    scope: null
  });
  
  const [conditionInput, setConditionInput] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [existingDocUrl, setExistingDocUrl] = useState(null);

  // Scope state per type
  const [stsScope, setStsScope] = useState({
    scope_type: 'sts',
    max_height_agl: 120,
    max_distance: '',
    geographic_limitations: '',
    airspace_classes: []
  });
  
  const [pdraScope, setPdraScope] = useState({
    scope_type: 'pdra',
    operational_volume: '',
    ground_risk_class: '',
    air_risk_class: '',
    containment_requirements: ''
  });
  
  const [lucScope, setLucScope] = useState({
    scope_type: 'luc',
    privileges: [],
    max_sail_level: '',
    drone_classes: [],
    oma_reference: ''
  });
  
  const [customScope, setCustomScope] = useState({
    scope_type: 'custom',
    description: '',
    limitations: []
  });
  
  const [privilegeInput, setPrivilegeInput] = useState('');
  const [limitationInput, setLimitationInput] = useState('');

  // Dirty-guard: snapshot the form once it reflects its "clean" baseline (default
  // values for create, fetched values for edit — captured right after `loading`
  // turns false, so it's taken from the same render as the fetched state), then
  // block an organization scope switch while the snapshot and current state differ.
  const initialSnapshotRef = useRef(null);
  const buildFormSnapshot = useCallback(() => JSON.stringify({
    form, stsScope, pdraScope, lucScope, customScope, uploadFileName: uploadFile?.name || null,
  }), [form, stsScope, pdraScope, lucScope, customScope, uploadFile]);

  useEffect(() => {
    if (!loading) {
      initialSnapshotRef.current = buildFormSnapshot();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  const isDirty = initialSnapshotRef.current !== null && buildFormSnapshot() !== initialSnapshotRef.current;

  useEffect(() => {
    if (!isDirty) return undefined;
    return registerScopeChangeGuard(() => window.confirm(
      t.scopeChangeConfirm || (language === 'cs'
        ? 'Neuložené změny v této autorizaci se ztratí. Opravdu chcete přepnout organizaci?'
        : 'Unsaved changes to this authorization will be lost. Switch organization anyway?')
    ));
  }, [isDirty, registerScopeChangeGuard, t.scopeChangeConfirm, language]);

  // Fetch existing authorization for edit mode
  const fetchAuthorization = useCallback(async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      const res = await api.get(`/authorizations/${id}`);
      const auth = res.data;
      
      // Populate form
      setForm({
        type: auth.type || 'STS_01',
        reference_number: auth.reference_number || '',
        title: auth.title || '',
        issued_by: auth.issued_by || 'ÚCL',
        issued_at: auth.issued_at ? auth.issued_at.split('T')[0] : '',
        valid_from: auth.valid_from ? auth.valid_from.split('T')[0] : '',
        valid_until: auth.valid_until ? auth.valid_until.split('T')[0] : '',
        conditions: auth.conditions || [],
        applicable_sail_levels: auth.applicable_sail_levels || [],
        aircraft_ids: auth.aircraft_ids || [],
        notes: auth.notes || '',
        scope: auth.scope
      });
      
      // Populate scope based on type
      if (auth.scope) {
        const scopeType = auth.scope.scope_type;
        if (scopeType === 'sts') {
          setStsScope({
            scope_type: 'sts',
            max_height_agl: auth.scope.max_height_agl || 120,
            max_distance: auth.scope.max_distance || '',
            geographic_limitations: auth.scope.geographic_limitations || '',
            airspace_classes: auth.scope.airspace_classes || []
          });
        } else if (scopeType === 'pdra') {
          setPdraScope({
            scope_type: 'pdra',
            operational_volume: auth.scope.operational_volume || '',
            ground_risk_class: auth.scope.ground_risk_class || '',
            air_risk_class: auth.scope.air_risk_class || '',
            containment_requirements: auth.scope.containment_requirements || ''
          });
        } else if (scopeType === 'luc') {
          setLucScope({
            scope_type: 'luc',
            privileges: auth.scope.privileges || [],
            max_sail_level: auth.scope.max_sail_level || '',
            drone_classes: auth.scope.drone_classes || [],
            oma_reference: auth.scope.oma_reference || ''
          });
        } else if (scopeType === 'custom') {
          setCustomScope({
            scope_type: 'custom',
            description: auth.scope.description || '',
            limitations: auth.scope.limitations || []
          });
        }
      }
      
      setExistingDocUrl(auth.document_url);
    } catch (err) {
      console.error('Failed to fetch authorization:', err);
      toast.error(t.loadError || 'Failed to load authorization');
      navigate('/authorizations');
    } finally {
      setLoading(false);
    }
  }, [id, navigate, t]);

  useEffect(() => {
    fetchAuthorization();
  }, [fetchAuthorization]);

  // Listen for language changes
  useEffect(() => {
    const handleStorage = () => {
      setLanguage(localStorage.getItem('language') || 'cs');
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  // Get scope type for the selected authorization type
  const getScopeType = (authType) => {
    if (authType === 'STS_01' || authType === 'STS_02') return 'sts';
    if (authType.startsWith('PDRA')) return 'pdra';
    if (authType === 'LUC') return 'luc';
    if (authType === 'CUSTOM') return 'custom';
    return null; // No scope UI for OPERATIONAL_AUTHORIZATION
  };

  // Get scope data based on type
  const getScopeData = () => {
    const scopeType = getScopeType(form.type);
    
    if (scopeType === 'sts') {
      return {
        ...stsScope,
        max_distance: stsScope.max_distance ? parseInt(stsScope.max_distance) : null
      };
    }
    if (scopeType === 'pdra') return pdraScope;
    if (scopeType === 'luc') {
      return {
        ...lucScope,
        max_sail_level: lucScope.max_sail_level ? parseInt(lucScope.max_sail_level) : null
      };
    }
    if (scopeType === 'custom') return customScope;
    return null;
  };

  // Add condition tag
  const handleAddCondition = () => {
    if (conditionInput.trim() && !form.conditions.includes(conditionInput.trim())) {
      setForm({ ...form, conditions: [...form.conditions, conditionInput.trim()] });
      setConditionInput('');
    }
  };

  // Remove condition tag
  const handleRemoveCondition = (condition) => {
    setForm({ ...form, conditions: form.conditions.filter(c => c !== condition) });
  };

  // Toggle SAIL level
  const handleToggleSail = (level) => {
    const current = form.applicable_sail_levels;
    if (current.includes(level)) {
      setForm({ ...form, applicable_sail_levels: current.filter(l => l !== level) });
    } else {
      setForm({ ...form, applicable_sail_levels: [...current, level].sort((a, b) => 
        SAIL_LEVELS.indexOf(a) - SAIL_LEVELS.indexOf(b)
      ) });
    }
  };

  // Toggle airspace class for STS scope
  const handleToggleAirspace = (cls) => {
    const current = stsScope.airspace_classes;
    if (current.includes(cls)) {
      setStsScope({ ...stsScope, airspace_classes: current.filter(c => c !== cls) });
    } else {
      setStsScope({ ...stsScope, airspace_classes: [...current, cls].sort() });
    }
  };

  // Toggle drone class for LUC scope
  const handleToggleDroneClass = (cls) => {
    const current = lucScope.drone_classes;
    if (current.includes(cls)) {
      setLucScope({ ...lucScope, drone_classes: current.filter(c => c !== cls) });
    } else {
      setLucScope({ ...lucScope, drone_classes: [...current, cls].sort() });
    }
  };

  // Add privilege for LUC
  const handleAddPrivilege = () => {
    if (privilegeInput.trim() && !lucScope.privileges.includes(privilegeInput.trim())) {
      setLucScope({ ...lucScope, privileges: [...lucScope.privileges, privilegeInput.trim()] });
      setPrivilegeInput('');
    }
  };

  // Add limitation for Custom
  const handleAddLimitation = () => {
    if (limitationInput.trim() && !customScope.limitations.includes(limitationInput.trim())) {
      setCustomScope({ ...customScope, limitations: [...customScope.limitations, limitationInput.trim()] });
      setLimitationInput('');
    }
  };

  // Save authorization
  const handleSave = async () => {
    // Validation
    if (!form.reference_number || !form.title || !form.issued_at || !form.valid_from || !form.valid_until) {
      toast.error(language === 'cs' ? 'Vyplňte všechna povinná pole' : 'Fill in all required fields');
      return;
    }

    if (form.applicable_sail_levels.length === 0) {
      toast.error(language === 'cs' ? 'Vyberte alespoň jednu úroveň SAIL' : 'Select at least one SAIL level');
      return;
    }

    if (new Date(form.valid_from) >= new Date(form.valid_until)) {
      toast.error(language === 'cs' ? 'Datum "Platnost od" musí být před "Platnost do"' : 'Valid from must be before valid until');
      return;
    }

    setSaving(true);
    try {
      const scopeData = getScopeData();
      
      const payload = {
        type: form.type,
        reference_number: form.reference_number,
        title: form.title,
        issued_by: form.issued_by,
        issued_at: new Date(form.issued_at).toISOString(),
        valid_from: new Date(form.valid_from).toISOString(),
        valid_until: new Date(form.valid_until).toISOString(),
        conditions: form.conditions,
        applicable_sail_levels: form.applicable_sail_levels,
        aircraft_ids: form.aircraft_ids,
        notes: form.notes || null,
        scope: scopeData
      };

      let authId;
      if (isEditMode) {
        await api.put(`/authorizations/${id}`, payload);
        authId = id;
        toast.success(t.authorizationUpdated);
      } else {
        const res = await api.post('/authorizations', payload);
        authId = res.data.id;
        toast.success(t.authorizationCreated);
      }

      // Upload PDF if selected
      if (uploadFile && authId) {
        setUploading(true);
        const formData = new FormData();
        formData.append('file', uploadFile);
        await api.post(`/authorizations/${authId}/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success(t.documentUploaded);
      }

      navigate('/authorizations');
    } catch (err) {
      console.error('Save failed:', err);
      toast.error(err.response?.data?.detail || t.saveError || 'Failed to save authorization');
    } finally {
      setSaving(false);
      setUploading(false);
    }
  };

  // Render scope fields based on type
  const renderScopeFields = () => {
    const scopeType = getScopeType(form.type);
    
    if (!scopeType) {
      return (
        <div className="text-center py-6 text-[var(--text-muted)]">
          <Info className="h-8 w-8 mx-auto mb-2" />
          <p>{t.scope?.comingSoon || 'Scope details coming soon for this type'}</p>
        </div>
      );
    }

    if (scopeType === 'sts') {
      return (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t.scope?.maxHeight || 'Max Height AGL (m)'} *</Label>
              <Input
                type="number"
                value={stsScope.max_height_agl}
                onChange={(e) => setStsScope({ ...stsScope, max_height_agl: parseInt(e.target.value) || 120 })}
                min={0}
                max={500}
                data-testid="scope-max-height"
              />
            </div>
            <div className="space-y-2">
              <Label>{t.scope?.maxDistance || 'Max Distance (m)'}</Label>
              <Input
                type="number"
                value={stsScope.max_distance}
                onChange={(e) => setStsScope({ ...stsScope, max_distance: e.target.value })}
                placeholder={form.type === 'STS_02' ? '2000' : ''}
                data-testid="scope-max-distance"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>{t.scope?.geographicLimitations || 'Geographic Limitations'}</Label>
            <Textarea
              value={stsScope.geographic_limitations}
              onChange={(e) => setStsScope({ ...stsScope, geographic_limitations: e.target.value })}
              rows={2}
              data-testid="scope-geo-limitations"
            />
          </div>
          <div className="space-y-2">
            <Label>{t.scope?.airspaceClasses || 'Airspace Classes'}</Label>
            <div className="flex flex-wrap gap-2">
              {AIRSPACE_CLASSES.map(cls => (
                <label 
                  key={cls}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
                    stsScope.airspace_classes.includes(cls)
                      ? 'bg-blue-50 border-blue-300 text-blue-700'
                      : 'bg-white border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <Checkbox
                    checked={stsScope.airspace_classes.includes(cls)}
                    onCheckedChange={() => handleToggleAirspace(cls)}
                  />
                  <span className="text-sm font-medium">{cls}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      );
    }

    if (scopeType === 'pdra') {
      return (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>{t.scope?.operationalVolume || 'Operational Volume'}</Label>
            <Textarea
              value={pdraScope.operational_volume}
              onChange={(e) => setPdraScope({ ...pdraScope, operational_volume: e.target.value })}
              rows={2}
              data-testid="scope-op-volume"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t.scope?.groundRiskClass || 'Ground Risk Class (GRC)'}</Label>
              <Input
                value={pdraScope.ground_risk_class}
                onChange={(e) => setPdraScope({ ...pdraScope, ground_risk_class: e.target.value })}
                placeholder="e.g., GRC-2"
                data-testid="scope-grc"
              />
            </div>
            <div className="space-y-2">
              <Label>{t.scope?.airRiskClass || 'Air Risk Class (ARC)'}</Label>
              <Input
                value={pdraScope.air_risk_class}
                onChange={(e) => setPdraScope({ ...pdraScope, air_risk_class: e.target.value })}
                placeholder="e.g., ARC-b"
                data-testid="scope-arc"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>{t.scope?.containmentRequirements || 'Containment Requirements'}</Label>
            <Textarea
              value={pdraScope.containment_requirements}
              onChange={(e) => setPdraScope({ ...pdraScope, containment_requirements: e.target.value })}
              rows={2}
              data-testid="scope-containment"
            />
          </div>
        </div>
      );
    }

    if (scopeType === 'luc') {
      return (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t.scope?.maxSailLevel || 'Max SAIL Level'} *</Label>
              <Select 
                value={lucScope.max_sail_level?.toString() || ''} 
                onValueChange={(v) => setLucScope({ ...lucScope, max_sail_level: v })}
              >
                <SelectTrigger data-testid="scope-max-sail">
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent>
                  {[1, 2, 3, 4, 5, 6].map(level => (
                    <SelectItem key={level} value={level.toString()}>SAIL {['I', 'II', 'III', 'IV', 'V', 'VI'][level - 1]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t.scope?.omaReference || 'OMA Reference'}</Label>
              <Input
                value={lucScope.oma_reference}
                onChange={(e) => setLucScope({ ...lucScope, oma_reference: e.target.value })}
                placeholder="e.g., OMA-2024-001"
                data-testid="scope-oma"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>{t.scope?.droneClasses || 'Drone Classes'}</Label>
            <div className="flex flex-wrap gap-2">
              {DRONE_CLASSES.map(cls => (
                <label 
                  key={cls}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
                    lucScope.drone_classes.includes(cls)
                      ? 'bg-purple-50 border-purple-300 text-purple-700'
                      : 'bg-white border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <Checkbox
                    checked={lucScope.drone_classes.includes(cls)}
                    onCheckedChange={() => handleToggleDroneClass(cls)}
                  />
                  <span className="text-sm font-medium">{cls}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            <Label>{t.scope?.privileges || 'Privileges'}</Label>
            <div className="flex gap-2">
              <Input
                value={privilegeInput}
                onChange={(e) => setPrivilegeInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddPrivilege(); } }}
                placeholder={language === 'cs' ? 'e.g., self_authorize_ConOps' : 'e.g., self_authorize_ConOps'}
              />
              <Button type="button" variant="outline" onClick={handleAddPrivilege}>
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            {lucScope.privileges.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {lucScope.privileges.map((priv, i) => (
                  <Badge key={i} variant="outline" className="gap-1 pr-1">
                    {priv}
                    <button
                      onClick={() => setLucScope({ ...lucScope, privileges: lucScope.privileges.filter((_, idx) => idx !== i) })}
                      className="ml-1 hover:bg-slate-200 rounded p-0.5"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </div>
      );
    }

    if (scopeType === 'custom') {
      return (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>{t.scope?.description || 'Description'} *</Label>
            <Textarea
              value={customScope.description}
              onChange={(e) => setCustomScope({ ...customScope, description: e.target.value })}
              rows={3}
              data-testid="scope-description"
            />
          </div>
          <div className="space-y-2">
            <Label>{t.scope?.limitations || 'Limitations'}</Label>
            <div className="flex gap-2">
              <Input
                value={limitationInput}
                onChange={(e) => setLimitationInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddLimitation(); } }}
                placeholder={language === 'cs' ? 'Zadejte omezení...' : 'Enter limitation...'}
              />
              <Button type="button" variant="outline" onClick={handleAddLimitation}>
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            {customScope.limitations.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {customScope.limitations.map((lim, i) => (
                  <Badge key={i} variant="outline" className="gap-1 pr-1">
                    {lim}
                    <button
                      onClick={() => setCustomScope({ ...customScope, limitations: customScope.limitations.filter((_, idx) => idx !== i) })}
                      className="ml-1 hover:bg-slate-200 rounded p-0.5"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </div>
      );
    }

    return null;
  };

  // Page actions
  const pageActions = (
    <div className="flex gap-2">
      <Button variant="outline" onClick={() => navigate('/authorizations')}>
        {tCommon.cancel}
      </Button>
      <Button 
        onClick={handleSave} 
        disabled={saving || uploading}
        className="gap-2 bg-[var(--brand-600)] hover:bg-[var(--brand-700)]"
        data-testid="save-auth-btn"
      >
        {(saving || uploading) ? (
          <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
        ) : (
          <Save className="h-4 w-4" />
        )}
        {language === 'cs' ? 'Uložit' : 'Save'}
      </Button>
    </div>
  );

  return (
    <PageShell
      title={isEditMode ? t.editAuthorization : t.addAuthorization}
      icon={Award}
      backPath="/authorizations"
      actions={pageActions}
      loading={loading}
    >
      <PageShell.Content className="space-y-6 max-w-4xl">
        {/* Basic Information */}
        <Card>
          <CardHeader>
            <CardTitle>{t.authorizationDetails}</CardTitle>
            <CardDescription>{t.subtitle}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="type">{t.authorizationType} *</Label>
                <Select 
                  value={form.type} 
                  onValueChange={(v) => setForm({ ...form, type: v })}
                >
                  <SelectTrigger data-testid="auth-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {AUTH_TYPES.map(type => (
                      <SelectItem key={type} value={type}>{t.types?.[type] || type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="reference_number">{t.referenceNumber} *</Label>
                <Input
                  id="reference_number"
                  value={form.reference_number}
                  onChange={(e) => setForm({ ...form, reference_number: e.target.value })}
                  placeholder="UCL-2024-SPEC-001"
                  data-testid="ref-number-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="title">{t.authTitle} *</Label>
              <Input
                id="title"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder={language === 'cs' ? 'Autorizace pro BVLOS operace' : 'BVLOS operations authorization'}
                data-testid="title-input"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="issued_by">{t.issuingAuthority}</Label>
                <Input
                  id="issued_by"
                  value={form.issued_by}
                  onChange={(e) => setForm({ ...form, issued_by: e.target.value })}
                  placeholder="ÚCL"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="issued_at">{t.issuedAt} *</Label>
                <Input
                  id="issued_at"
                  type="date"
                  value={form.issued_at}
                  onChange={(e) => setForm({ ...form, issued_at: e.target.value })}
                  data-testid="issued-at-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="valid_from">{t.validFrom} *</Label>
                <Input
                  id="valid_from"
                  type="date"
                  value={form.valid_from}
                  onChange={(e) => setForm({ ...form, valid_from: e.target.value })}
                  data-testid="valid-from-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="valid_until">{t.validUntil} *</Label>
                <Input
                  id="valid_until"
                  type="date"
                  value={form.valid_until}
                  onChange={(e) => setForm({ ...form, valid_until: e.target.value })}
                  data-testid="valid-until-input"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Scope Details */}
        <Card>
          <CardHeader>
            <CardTitle>{t.scope?.title || 'Scope Details'}</CardTitle>
            <CardDescription>
              {language === 'cs' 
                ? `Specifické parametry pro typ ${t.types?.[form.type] || form.type}`
                : `Type-specific parameters for ${t.types?.[form.type] || form.type}`
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            {renderScopeFields()}
          </CardContent>
        </Card>

        {/* SAIL Levels */}
        <Card>
          <CardHeader>
            <CardTitle>{t.sailLevels} *</CardTitle>
            <CardDescription>{t.sailLevelsHint}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {SAIL_LEVELS.map(level => (
                <label 
                  key={level}
                  className={`flex items-center gap-2 px-4 py-3 rounded-lg border cursor-pointer transition-colors ${
                    form.applicable_sail_levels.includes(level)
                      ? 'bg-blue-50 border-blue-300 text-blue-700'
                      : 'bg-white border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <Checkbox
                    checked={form.applicable_sail_levels.includes(level)}
                    onCheckedChange={() => handleToggleSail(level)}
                  />
                  <span className="text-sm font-medium">SAIL {level}</span>
                </label>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Conditions */}
        <Card>
          <CardHeader>
            <CardTitle>{t.conditions}</CardTitle>
            <CardDescription>{t.conditionsHint}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2">
              <Input
                value={conditionInput}
                onChange={(e) => setConditionInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddCondition(); } }}
                placeholder={language === 'cs' ? 'Max výška 120m AGL...' : 'Max altitude 120m AGL...'}
              />
              <Button type="button" variant="outline" onClick={handleAddCondition}>
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            {form.conditions.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-4">
                {form.conditions.map((cond, i) => (
                  <Badge key={i} variant="outline" className="gap-1 pr-1">
                    <Tag className="h-3 w-3" />
                    {cond}
                    <button
                      onClick={() => handleRemoveCondition(cond)}
                      className="ml-1 hover:bg-slate-200 rounded p-0.5"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Notes */}
        <Card>
          <CardHeader>
            <CardTitle>{t.notes}</CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={3}
              data-testid="notes-input"
            />
          </CardContent>
        </Card>

        {/* PDF Upload */}
        <Card>
          <CardHeader>
            <CardTitle>{t.uploadPdf}</CardTitle>
          </CardHeader>
          <CardContent>
            <div 
              className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                uploadFile ? 'border-green-300 bg-green-50' : 'border-slate-200 hover:border-slate-300'
              }`}
              onClick={() => document.getElementById('pdf-upload')?.click()}
            >
              <input
                id="pdf-upload"
                type="file"
                accept=".pdf"
                className="hidden"
                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
              />
              {uploadFile ? (
                <div className="flex items-center justify-center gap-2 text-green-700">
                  <FileText className="h-6 w-6" />
                  <span className="font-medium">{uploadFile.name}</span>
                  <button 
                    onClick={(e) => { e.stopPropagation(); setUploadFile(null); }}
                    className="ml-2 text-red-500 hover:text-red-600"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              ) : existingDocUrl ? (
                <div className="text-slate-600">
                  <FileText className="h-8 w-8 mx-auto mb-2" />
                  <p className="font-medium">{t.pdfUploaded}</p>
                  <p className="text-sm text-blue-600 mt-1">{t.replacePdf}</p>
                </div>
              ) : (
                <div className="text-slate-500">
                  <Upload className="h-8 w-8 mx-auto mb-2" />
                  <p>{t.dropPdfHere}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </PageShell.Content>
    </PageShell>
  );
}
